import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Scales, ShieldCheck, FileText, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const SLOT_LABELS = {
  header: "Financial Promotion Notice",
  footer: "Full Investor Disclaimer",
  confidential: "Confidentiality Undertaking",
  sign_in: "Platform Access Notice",
};

export default function InvestorDeclarationModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disclaimers, setDisclaimers] = useState({});
  const [chkPro, setChkPro] = useState(false);
  const [chkConf, setChkConf] = useState(false);
  const [chkDisc, setChkDisc] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Only investors need to accept the FCA declaration
    if (user.role !== "investor") { setLoading(false); return; }
    (async () => {
      try {
        const [status, active] = await Promise.all([
          api.get("/investor-declaration/status"),
          api.get("/disclaimers/active"),
        ]);
        setDisclaimers(active.data || {});
        if (status.data.needs_declaration) setOpen(true);
      } catch {
        setOpen(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const submit = async () => {
    if (!(chkPro && chkConf && chkDisc)) {
      toast.error("Please confirm all three statements to continue.");
      return;
    }
    setBusy(true);
    try {
      const ids = Object.values(disclaimers).map((d) => d.id);
      await api.post("/investor-declaration/accept", {
        disclaimer_ids: ids,
        professional_investor_confirmed: chkPro,
        confidentiality_agreed: chkConf,
        disclaimer_accepted: chkDisc,
      });
      toast.success("Declaration recorded. Welcome to the portal.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record declaration");
    } finally { setBusy(false); }
  };

  if (loading || !open) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* forced modal — cannot dismiss */ }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-[11px] uppercase tracking-[0.2em] font-bold">
            <Warning size={14} weight="fill" /> FCA Investor Declaration — required to continue
          </div>
          <DialogTitle className="font-display text-3xl mt-2">Professional Investor Declaration</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground leading-relaxed">
          Before accessing investment opportunities on Inventya Capital, please read the following disclaimers
          from our FCA compliance partner and confirm the three statements at the bottom of this page.
        </p>

        <div className="space-y-4 mt-2" data-testid="declaration-disclaimers">
          {Object.entries(disclaimers).map(([slot, d]) => (
            <div key={slot} className="rounded-xl border border-border p-4 bg-secondary/30">
              <div className="flex items-center gap-2 mb-2">
                {slot === "header" && <Scales size={16} className="text-emerald-700" />}
                {slot === "footer" && <FileText size={16} className="text-emerald-700" />}
                {slot === "confidential" && <ShieldCheck size={16} className="text-emerald-700" />}
                {slot === "sign_in" && <Warning size={16} className="text-emerald-700" />}
                <div className="font-display font-bold text-sm">{SLOT_LABELS[slot] || slot}</div>
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line max-h-40 overflow-y-auto pr-2">
                {d.body}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <Checkbox checked={chkPro} onCheckedChange={setChkPro} data-testid="chk-pro-investor" className="mt-0.5" />
            <span>I confirm that I am an <b>eligible Professional Investor</b> as defined in Article 19 of the Financial Services and Markets Act 2000 (Financial Promotion) Order 2005.</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <Checkbox checked={chkConf} onCheckedChange={setChkConf} data-testid="chk-confidentiality" className="mt-0.5" />
            <span>I agree to the <b>confidentiality undertaking</b> above and will not reproduce, distribute or disclose any materials accessed on this platform.</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <Checkbox checked={chkDisc} onCheckedChange={setChkDisc} data-testid="chk-disclaimer" className="mt-0.5" />
            <span>I have read and accept the <b>current disclaimers (v1.0)</b> in their entirety.</span>
          </label>
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground">
          Your acceptance is logged for compliance audit — IP address, timestamp and disclaimer version references
          will be stored against your account.
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            onClick={submit}
            disabled={busy || !(chkPro && chkConf && chkDisc)}
            data-testid="accept-declaration-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {busy ? "Recording…" : "Accept & Continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
